import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import Button from '@insurancemenu/imenu-components-lib/dist/components/Button';
import ActionModal from '@insurancemenu/imenu-components-lib/dist/components/modals/ActionModal';
import { Add } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRFQPlanAttributesPageStore } from 'store/pages/rfq/rfq-template/plan-attributes/pageStore';
import useRfqTemplate from 'global/hooks/useRfqTemplate';
import { colors, OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import { PlanAttributeCategory } from 'types/productServiceV2';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import UncompleteStepSection from 'components/common/rfq/UncompleteStepSection';
import {
  ContentStyled,
  AttributesProgressStatusStyled,
  FooterStyled,
  AddAttributeButtonStyled,
  NoDataMessageStyled,
  CustomAttributesSectionStyled,
  RemoveAttributeLabelStyled,
} from './styles';
import {
  RFQ_PLAN_ATTRIBUTES_SPINNER_TEST_ID,
  RFQ_PLAN_ATTRIBUTES_ACTION_BTN_TEST_ID,
  RFQ_PLAN_ATTRIBUTES_BACK_BTN_TEST_ID,
  WARNING_MODAL_TEST_ID,
} from '../constants';
import AttributesSection from '../../../common/rfq/AttributesSection';
import PlanAttributesList from './PlanAttributesList';

const AddAttributeButton: React.FC = () => {
  const history = useHistory();
  const { templateId, pageType } = useRfqTemplate();

  const onAddAttribute = (): void => {
    history.push(
      `/configure-attribute/${templateId}?&attributeCategory=${PlanAttributeCategory.CUSTOM}&type=${pageType}`,
    );
  };
  return (
    <AddAttributeButtonStyled
      size="large"
      color="custom"
      customColor={colors.green[600]}
      leftIconProp={<Add />}
      onClick={onAddAttribute}
    >
      Add New Attribute
    </AddAttributeButtonStyled>
  );
};

const RFQPlanAttributes = observer((): JSX.Element => {
  // Common data
  const history = useHistory();
  const { templateId, pageType } = useRfqTemplate();

  const store = useRFQPlanAttributesPageStore();
  // Mobx store
  const {
    isLoading,
    requiredAttributes,
    customAttributes,
    areAllAttributesFilled,
    attributesProgressStatus,
    removeAttributeModalDelegate: {
      isRemoveAttributeModalOpen,
      attributeId,
      closeRemoveAttributeModal,
      removeCustomAttribute,
      modalLoading,
    },
    isStepCompleted,
    isTemplateInProgress,
    isStepConfigurable,
    showCustomAttributesFeature,
    getInitialData,
    completeStep,
    uncompleteStep,
    resetInitialData,
  } = store;

  useEffect(() => {
    void getInitialData();
    return resetInitialData;
  }, []);

  const returnBack = (): void => {
    history.push(`/product-template-checklist/${templateId}?type=${pageType}`);
  };

  const onCompleteStep = async (): Promise<void> => {
    await completeStep();
    returnBack();
  };

  return (
    <div>
      <AttributesProgressStatusStyled>{attributesProgressStatus}</AttributesProgressStatusStyled>
      {showCustomAttributesFeature && isStepConfigurable && <AddAttributeButton />}
      {isTemplateInProgress && isStepCompleted ? (
        <UncompleteStepSection isLoading={isLoading} onSubmit={uncompleteStep} />
      ) : null}

      <ContentStyled>
        <OverlaySpinner display={isLoading} data-test-id={RFQ_PLAN_ATTRIBUTES_SPINNER_TEST_ID} />
        <AttributesSection title="Required Attributes">
          <PlanAttributesList
            attributesInfo={requiredAttributes}
            isStepConfigurable={isStepConfigurable}
            isCustomAttribute={false}
          />
        </AttributesSection>

        {showCustomAttributesFeature && !isLoading && (
          <CustomAttributesSectionStyled title="Your Custom Attributes">
            {customAttributes.length > 0 ? (
              <PlanAttributesList
                attributesInfo={customAttributes}
                isStepConfigurable={isStepConfigurable}
                isCustomAttribute
              />
            ) : (
              <div>
                <NoDataMessageStyled>You haven&apos;t added any attributes yet</NoDataMessageStyled>
                {isStepConfigurable && <AddAttributeButton />}
              </div>
            )}
          </CustomAttributesSectionStyled>
        )}
      </ContentStyled>
      <FooterStyled>
        <Button
          type="outlined"
          size="large"
          width="24rem"
          onClick={returnBack}
          data-test-id={RFQ_PLAN_ATTRIBUTES_BACK_BTN_TEST_ID}
        >
          Back
        </Button>
        {isTemplateInProgress && (
          <Button
            size="large"
            width="24rem"
            color="primary"
            onClick={onCompleteStep}
            // If step is completed, firstly user has to uncomplete it before completing again
            disabled={!areAllAttributesFilled || isLoading || isStepCompleted}
            data-test-id={RFQ_PLAN_ATTRIBUTES_ACTION_BTN_TEST_ID}
          >
            {isStepCompleted ? 'Update' : 'Complete'}
          </Button>
        )}
      </FooterStyled>
      <ActionModal
        show={isRemoveAttributeModalOpen}
        title="Remove Attribute"
        subtitle={
          <span>
            Are you sure you want to remove <RemoveAttributeLabelStyled>{attributeId}</RemoveAttributeLabelStyled>{' '}
            attribute?
            <br /> This action will remove the attribute from your product template.
          </span>
        }
        actionButtonText="Remove"
        closeButtonText="Cancel"
        loading={modalLoading}
        onClose={closeRemoveAttributeModal}
        onAction={removeCustomAttribute}
        modalType={ModalType.DELETE}
        data-test-id={WARNING_MODAL_TEST_ID}
      />
    </div>
  );
});

export default RFQPlanAttributes;
